import Matrix from '../Math/Matrix';
import Vector from '../Math/Vector';

import PrivateVars from '../PrivateVars';
import ArrayUtil from '../ArrayUtil';

import Texture from './Texture';

let GL_STR_TO_TYPE = {
    'vec3': 35665,
    'vec2': 35664,
    'mat4': 35676,
    'sampler2D': 35678
};

let GL_TYPE_TO_STR = {};
for (let n in GL_STR_TO_TYPE) {
    let t = GL_STR_TO_TYPE[n];
    GL_TYPE_TO_STR[t] = n;
}

/**
 * Compiles a GLSL shader.
 * @param {WebGLRenderingContext} gl The GL context.
 * @param {*} type The shader type ( either gl.VERTEX_SHADER or gl.FRAGMENT_SHADER )
 * @param {*} source The shader source code
 */
function compileShader(gl, type, source) {
    // Create and compile shader
    let shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    // Check compilation status
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error(gl.getShaderInfoLog(shader));
        return undefined;
    } else {
        return shader;
    }
}

/**
 * Links a GLSL program from multiple shaders.
 * @param {WebGLRenderingContext} gl The GL context.
 * @param {*} shaders Multiple valid shaders.
 */
function compileProgram(gl, shaders) {
    // Create and link shader program.
    let program = gl.createProgram();
    shaders.forEach(shader => gl.attachShader(program, shader));
    gl.linkProgram(program);

    // Check link status
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.error(gl.getProgramInfoLog(program));
        return undefined;
    } else {
        return program;
    }
}

function getActiveAttributes(gl, program) {
    let attrCount = gl.getProgramParameter(program, gl.ACTIVE_ATTRIBUTES);

    let a = {};
    for (let i = 0; i < attrCount; i++) {
        let attr = gl.getActiveAttrib(program, i);
        let loc = gl.getAttribLocation(program, attr.name);

        // Store attribute ( by name )
        a[attr.name] = {
            name: attr.name,
            type: GL_TYPE_TO_STR[attr.type],
            size: attr.size,
            location: loc,
        }
    }

    return a;
}

function getActiveUniforms(gl, program) {
    let uniformCount = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);

    let a = {};
    for (let i = 0; i < uniformCount; i++) {
        let uniform = gl.getActiveUniform(program, i);
        let loc = gl.getUniformLocation(program, uniform.name);

        // Store uniform ( by name )
        a[uniform.name] = {
            name: uniform.name,
            type: GL_TYPE_TO_STR[uniform.type],
            size: uniform.size,
            location: loc,
        }
    }

    return a;
}

function ensureArray(v) {
    if (Array.isArray(v)) return v;
    else return [v];
}

let _ActiveShader = undefined;

let TEMP_MATRIX = new Matrix();

class Shader {

    /**
     * Gets the currently bound shader program.
     */
    static get ActiveShader() { return _ActiveShader; }

    constructor(renderer, source) {

        // 
        this.renderer = renderer;

        // 
        const gl = renderer.gl;
        let program = undefined;
        let shaders = {
            fragment: [],
            vertex: []
        };

        // Compile vertex shaders
        ensureArray(source.vertex).forEach(code =>
            shaders.vertex.push(compileShader(gl, gl.VERTEX_SHADER, source.vertex)));

        // Compile fragment shaders
        ensureArray(source.fragment).forEach(code =>
            shaders.fragment.push(compileShader(gl, gl.FRAGMENT_SHADER, source.fragment)));

        // Compile program
        program = compileProgram(gl, [].concat(shaders.fragment, shaders.vertex));

        // Extract Shader Reflection?
        let attributes = getActiveAttributes(gl, program);
        let uniforms = getActiveUniforms(gl, program);

        //
        PrivateVars.set(this, "shaders", shaders);
        PrivateVars.set(this, "program", program);
        PrivateVars.set(this, "attributes", attributes);
        PrivateVars.set(this, "uniforms", uniforms);

        // 
        PrivateVars.set(this, 'use', () => {
            //let program = PrivateVars.get(this, 'program');
            gl.useProgram(program);
            _ActiveShader = this;
        });

        // Expose
        this.data = PrivateVars.getAll(this);
    }

    dispose() {
        let gl = this.renderer.gl;
        gl.deleteProgram(this.data.program);
        this.shaders.fragment.forEach(shader => gl.deleteShader(shader));
        this.shaders.vertex.forEach(shader => gl.deleteShader(shader));
        this.data = undefined;
    }

    uniform(name, value) {
        const gl = this.renderer.gl;

        // Enable 
        if (_ActiveShader != this)
            PrivateVars.get(this, 'use')();

        let texUnit = 0;
        let uniforms = PrivateVars.get(this, "uniforms");
        if (name in uniforms) {
            // Coerce type based on uniform reflection
            let uniform = uniforms[name];
            switch (uniform.type) {

                // Coerce as Vec3 or Vec3[]
                case 'vec3':
                    // Single vector
                    if (value instanceof Vector) value = value.toArray();
                    else if (Array.isArray(value)) {
                        // Collapse array into a single set of floats
                        value = ArrayUtil.flatten(value, v => {
                            if (v instanceof Vector) return v.toArray();
                            else return v;
                        })
                    }

                    if (uniform.size == 1) {
                        gl.uniform3f(uniform.location, value[0], value[1], value[2]);
                    }
                    else {
                        gl.uniform3fv(uniform.location, new Float32Array(value));
                    }
                    break;

                case 'mat4':
                    if (uniform.size == 1) gl.uniformMatrix4fv(uniform.location, false, Matrix.transpose(value, TEMP_MATRIX).m);
                    else console.warn(`Unable to handle ${uniform.type} x ${uniform.size} uniform named '${name}'.`);
                    break;

                case 'sampler2D':
                    if (value instanceof Texture) {
                        let texture = PrivateVars.get(value, 'texture');
                        gl.uniform1i(uniform.location, texUnit);
                        gl.activeTexture(gl.TEXTURE0 + texUnit);
                        gl.bindTexture(gl.TEXTURE_2D, texture);
                        texUnit++;
                    } else {
                        throw new Error(`A none texture was given to uniform ${name}.`);
                    }
                    break;

                default:
                    console.warn(`Unable to handle ${uniform.type} x ${uniform.size} uniform named '${name}'.`);
                    break;
            }
        } else {
            console.warn(`Unable to find uniform named '${name}'.`);
        }
    }
}
 
export default Shader;